/**
 * Generic two-level tabs: primary group tabs ([data-tab-group]) and
 * sub-tabs within a group ([data-tab]). Panels are matched by
 * [data-panel-group] / [data-panel] and toggled with the hidden attribute.
 */
window.WsTabs = class WsTabs extends HTMLElement {
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    for (const tab of this.querySelectorAll('[data-tab-group]')) {
      tab.addEventListener('click', () => this.#selectGroup(tab.dataset.tabGroup), { signal });
    }

    for (const tab of this.querySelectorAll('[data-tab]')) {
      tab.addEventListener('click', () => this.#selectTab(tab.dataset.tab), { signal });
    }

    // Mobile: sub-tabs are a <select> dropdown
    for (const select of this.querySelectorAll('[data-tab-select]')) {
      select.addEventListener('change', () => this.#selectTab(select.value), { signal });
    }

    // Keyboard: arrow/Home/End move focus between tabs within a tablist.
    for (const tablist of this.querySelectorAll('[role="tablist"]')) {
      tablist.addEventListener('keydown', (event) => this.#onTabKeydown(event, tablist), { signal });
    }
  }

  #onTabKeydown(event, tablist) {
    const tabs = [...tablist.querySelectorAll('[role="tab"]')];
    const current = tabs.indexOf(document.activeElement);
    if (current < 0) return;

    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % tabs.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next === null) return;

    event.preventDefault();
    const tab = tabs[next];
    tab.focus();
    if (tab.dataset.tab) this.#selectTab(tab.dataset.tab);
    else if (tab.dataset.tabGroup) this.#selectGroup(tab.dataset.tabGroup);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #selectGroup(group) {
    for (const tab of this.querySelectorAll('[data-tab-group]')) {
      const selected = tab.dataset.tabGroup === group;
      tab.setAttribute('aria-selected', String(selected));
      if (tab.getAttribute('role') === 'tab') tab.tabIndex = selected ? 0 : -1;
    }
    for (const panel of this.querySelectorAll('[data-panel-group]')) {
      panel.hidden = panel.dataset.panelGroup !== group;
    }
  }

  #selectTab(id) {
    for (const tab of this.querySelectorAll('[data-tab]')) {
      tab.setAttribute('aria-expanded', String(tab.dataset.tab === id));
    }
    for (const panel of this.querySelectorAll('[data-panel]')) {
      panel.hidden = panel.dataset.panel !== id;
    }
    for (const select of this.querySelectorAll('[data-tab-select]')) {
      select.value = id;
    }
  }
}

if (!customElements.get('ws-tabs')) customElements.define('ws-tabs', WsTabs);
